/**
 * Product catalog for the H2HUMANS shop — Vite frontend edition.
 *
 * Adds image assets (Vite-imported) on top of the pure data layer from
 * product-catalog-data.ts. The data layer is the SOURCE OF TRUTH for
 * prices, SKUs, bundle composition, and Stripe price keys.
 *
 * Edge Functions import product-catalog-data.ts directly (no Vite deps).
 */

import {
  type ProductId,
  type CatalogId,
  SKU_MAP,
  PRODUCT_PRICES,
  BUNDLE_REQUIREMENTS,
  BUNDLE_PRODUCT_IDS,
  BUNDLE_INDIVIDUAL_TOTAL,
  BUNDLE_DATA,
} from "./product-catalog-data";

import previousSet from "../../public/images/products/hero/set-previous-gallery.webp.asset.json";
import { productImageUrl, productImageSrcSet } from "@/config/productHeroImages";
import bodyCareScoopAsset from "@/assets/daily-body-care-scoop.png.asset.json";
import bodyCareNutritionAsset from "@/assets/daily-body-care-nutrition.png.asset.json";
import bodyCareHeroAsset from "@/assets/body-care-previous-gallery.asset.json";
import bodyCareLifestyleAsset from "@/assets/daily-body-care-hero-v3.jpg.asset.json";
const bodyCareLandingImg = bodyCareHeroAsset.url;
const bodyCareDetailImg = bodyCareScoopAsset.url;
const bodyCareExtraImgs = [bodyCareLifestyleAsset.url, bodyCareNutritionAsset.url];

import hydrationOldHeroAsset from "@/assets/daily-hydration-hero.jpg.asset.json";

import hydrationNewHeroAsset from "@/assets/hydration-previous-gallery.asset.json";
import hydrationLemonsAsset from "@/assets/daily-hydration-lemons.png.asset.json";
import hydrationNutritionAsset from "@/assets/daily-hydration-nutrition.png.asset.json";
const hydrationLandingImg = hydrationNewHeroAsset.url;
const hydrationDetailImg = hydrationLemonsAsset.url;
const hydrationExtraImgs = [hydrationOldHeroAsset.url, hydrationNutritionAsset.url];
import balanceOldHeroAsset from "@/assets/daily-balance-hero.jpg.asset.json";
import balanceMoodAsset from "@/assets/balance-previous-gallery.asset.json";
import balanceLifestyleAsset from "@/assets/daily-balance-lifestyle.png.asset.json";
import balanceNutritionAsset from "@/assets/daily-balance-nutrition.png.asset.json";
const balanceLandingImg = balanceMoodAsset.url;
const balanceExtraImgs = [balanceOldHeroAsset.url, balanceLifestyleAsset.url, balanceNutritionAsset.url];
import balanceDetailImg from "@/assets/product-balance-detail.png";
/** Compatibility crop names; all new assets are selected centrally by role. */
export const SET_IMAGE_VARIANTS = { wide: '2x1', '3x2': '3x2', square: '1x1', '4x5': '4x5' } as const;
export type SetVariant = keyof typeof SET_IMAGE_VARIANTS;
export const setSrcSet = (v: SetVariant, ext: 'avif' | 'webp' | 'jpg') => productImageSrcSet('bundle', `hero-${SET_IMAGE_VARIANTS[v]}`, ext);
const bundleLandingImg = productImageUrl('bundle', 'thumb-1x1-clean');
export type { ProductId, CatalogId };

import type { LocalizedText } from "./product-catalog-data";
import { translations } from "./translations";

// Re-export shared types/constants so existing consumers keep working.
export { SKU_MAP, PRODUCT_PRICES, BUNDLE_REQUIREMENTS, BUNDLE_PRODUCT_IDS, BUNDLE_INDIVIDUAL_TOTAL, BUNDLE_DATA };

export interface LocalizedList {
  de: string[];
  en: string[];
  pt: string[];
}

/** Ingredient labels/values are chemistry + units — same in all locales. */
export interface Ingredient {
  label: string;
  value: string;
}

export interface Product {
  id: ProductId;
  sku: string;
  name: LocalizedText;
  description: LocalizedText;
  /** Active price charged in the cart and shown in the UI. Discounts come from promo codes. */
  price: number;
  /** Manufacturer's suggested retail price (UVP). Displayed strikethrough next to the active price. */
  msrp: number;
  /** Card image used on the order overview catalog. */
  image: string;
  /** Hero image for the landing product card (front face). */
  landingImage: string;
  /** Secondary image for the landing product card (ingredients face). */
  landingDetailImage: string;
  /** Additional gallery images shown in the slide-in detail panel. */
  extraImages?: string[];
  benefits: LocalizedList;
  ingredients: Ingredient[];
}

export const PRODUCTS: Product[] = [
  {
    id: "body-care",
    sku: SKU_MAP["body-care"],
    name: { de: "Daily Body-Care", en: "Daily Body-Care", pt: "Daily Body-Care" },
    description: {
      de: translations.de.products.bodycare.description,
      en: translations.en.products.bodycare.description,
      pt: translations.pt.products.bodycare.description,
    },
    price: PRODUCT_PRICES["body-care"],
    msrp: 69.9,
    image: productImageUrl("body-care", "thumb-1x1-clean"),
    landingImage: bodyCareLandingImg,
    landingDetailImage: bodyCareDetailImg,
    extraImages: bodyCareExtraImgs,
    benefits: {
      de: ["Kollagen-Komplex", "Biotin & Zink", "Antioxidantien"],
      en: ["Collagen Complex", "Biotin & Zinc", "Antioxidants"],
      pt: ["Complexo de colagénio", "Biotina e zinco", "Antioxidantes"],
    },
    ingredients: [
      { label: "Eiweiß", value: "22,4 g" },
      { label: "Kollagen Hydrolysat", value: "3000 mg" },
      { label: "Kreatinmonohydrat", value: "3000 mg" },
      { label: "L-Carnitin-Tartrat", value: "500 mg" },
      { label: "Kakao-Polyphenol-Extrakt", value: "250 mg" },
      { label: "Enzym Komplex", value: "40 mg" },
      { label: "B. coagulans", value: "2 Mrd. CFU" },
      { label: "Vitamin B6", value: "1,4 mg" },
      { label: "Vitamin B12", value: "2,89 \u00b5g" },
      { label: "Brennwert", value: "467 kJ / 111 kcal" },
      { label: "Fett", value: "1,2 g" },
      { label: "davon gesättigte Fettsäuren", value: "0,8 g" },
      { label: "Kohlenhydrate", value: "1,2 g" },
      { label: "davon Zucker", value: "0,6 g" },
      { label: "Ballaststoffe", value: "2,4 g" },
      { label: "Salz", value: "0,3 g" },
    ],
  },
  {
    id: "hydration",
    sku: SKU_MAP["hydration"],
    name: { de: "Daily Hydration", en: "Daily Hydration", pt: "Daily Hydration" },
    description: {
      de: translations.de.products.hydration.description,
      en: translations.en.products.hydration.description,
      pt: translations.pt.products.hydration.description,
    },
    price: PRODUCT_PRICES["hydration"],
    msrp: 29.9,
    image: productImageUrl("hydration", "thumb-1x1-clean"),
    landingImage: hydrationLandingImg,
    landingDetailImage: hydrationDetailImg,
    extraImages: hydrationExtraImgs,
    benefits: {
      de: ["Elektrolyte", "Mineralien", "Spurenelemente"],
      en: ["Electrolytes", "Minerals", "Trace Elements"],
      pt: ["Eletrólitos", "Minerais", "Oligoelementos"],
    },
    ingredients: [
      { label: "Natrium", value: "810 mg" },
      { label: "Chlorid", value: "1240 mg" },
      { label: "Kalium", value: "300 mg" },
      { label: "Magnesium", value: "100 mg" },
      { label: "Brennwert", value: "27 kJ / 6 kcal" },
      { label: "Salz", value: "2,1 g" },
      { label: "Eiweiß", value: "<0,5 g" },
      { label: "Fett", value: "<0,5 g" },
      { label: "davon gesättigte Fettsäuren", value: "0,0 g" },
      { label: "Kohlenhydrate", value: "<0,5 g" },
      { label: "davon Zucker", value: "<0,5 g" },
      { label: "Ballaststoffe", value: "<0,5 g" },
    ],
  },
  {
    id: "balance",
    sku: SKU_MAP["balance"],
    name: { de: "Daily Balance", en: "Daily Balance", pt: "Daily Balance" },
    description: {
      de: translations.de.products.balance.description,
      en: translations.en.products.balance.description,
      pt: translations.pt.products.balance.description,
    },
    price: PRODUCT_PRICES["balance"],
    msrp: 19.9,
    image: productImageUrl("balance", "thumb-1x1-clean"),
    landingImage: balanceLandingImg,
    landingDetailImage: balanceDetailImg,
    extraImages: balanceExtraImgs,
    benefits: {
      de: ["Adaptogene", "B-Vitamine", "Magnesium"],
      en: ["Adaptogens", "B-Vitamins", "Magnesium"],
      pt: ["Adaptogénios", "Vitaminas B", "Magnésio"],
    },
    ingredients: [
      { label: "Magnesium", value: "72,29 mg" },
      { label: "Vitamin D3", value: "25 \u00b5g" },
      { label: "Vitamin B12", value: "30 \u00b5g" },
      { label: "Zink", value: "5 mg" },
      { label: "Chrom", value: "25 \u00b5g" },
      { label: "Biotin", value: "500 \u00b5g" },
      { label: "Jod", value: "100 \u00b5g" },
      { label: "davon aus Magnesiumoxid", value: "30 mg" },
      { label: "davon aus Magnesiumbisglycinat", value: "42,29 mg" },
      { label: "Mangan", value: "1,5 mg" },
      { label: "Molybdän", value: "25 \u00b5g" },
      { label: "Bor", value: "0,5 mg" },
      { label: "Vitamin A", value: "120 \u00b5g RE" },
      { label: "Selen", value: "50 \u00b5g" },
      { label: "Protein (Spirulinapulver)", value: "50 mg" },
      { label: "Vitamin B1", value: "1,7 mg" },
      { label: "Vitamin B2", value: "2,1 mg" },
      { label: "Niacin", value: "20 mg NE" },
      { label: "Pantothensäure", value: "8,78 mg" },
      { label: "Vitamin B6", value: "2,8 mg" },
      { label: "Folsäure", value: "200 \u00b5g" },
      { label: "Vitamin C", value: "60 mg" },
      { label: "Vitamin E", value: "12 mg \u03b1-TE" },
      { label: "Vitamin K2", value: "50 \u00b5g" },
      { label: "Kupfer", value: "1000 \u00b5g" },
    ],
  },
];

// The BUNDLE version WITHOUT image (for data-layer consumers) is in
// product-catalog-data.ts. This one adds the Vite image import.
export const BUNDLE = {
  ...BUNDLE_DATA,
  /** 1:1 crop for tiles, carts, checkout overview. */
  image: bundleLandingImg,
  imageSrcSet: productImageSrcSet("bundle", "thumb-1x1-clean", "webp"),
  /** 4:5 crop for the set product gallery. */
  galleryImage: previousSet.url,
  gallerySrcSet: undefined,
  ogImage: `https://h2humans.com${productImageUrl("bundle", "og-1200x630")}`,
} as const;

/** For backwards compatibility — was previously computed from PRODUCTS. */
export const BUNDLE_SAVINGS = Math.max(0, BUNDLE_INDIVIDUAL_TOTAL - BUNDLE.price);

// Re-export STRIPE_PRICE_IDS from the shared data layer.
export { STRIPE_PRICE_IDS, PRODUCT_PAGE_CONTENT, SHOP_ORDER, getCatalogIdBySlug, getProductPath } from "./product-catalog-data";
export type { ProductPageContent } from "./product-catalog-data";

export const getProductById = (id: string): Product | undefined => PRODUCTS.find((p) => p.id === id);

/** Catalog entry usable directly with CartContext.addItem. */
export const getCatalogItem = (
  id: CatalogId,
): { id: string; name: LocalizedText; price: number; isBundle?: boolean } | undefined => {
  if (id === "bundle") {
    return { id: BUNDLE.id, name: BUNDLE.name, price: BUNDLE.price, isBundle: true };
  }
  const p = getProductById(id);
  return p ? { id: p.id, name: p.name, price: p.price } : undefined;
};
